import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ArrowLeft, Home, Users, Shield, Calendar } from "lucide-react";
import { useApp } from "../context/AppContext";
import { resolveImg } from "../lib/api";
import { Button } from "./ui/button";

const ICONS = { home: Home, users: Users, shield: Shield, calendar: Calendar };

export default function Hero() {
  const { t, lang, settings, L } = useApp();
  const nav = useNavigate();
  const hero = settings?.hero || {};
  const img = resolveImg(settings?.hero_image_url) ||
    "https://images.unsplash.com/photo-1720118509152-2df877673bee?crop=entropy&cs=srgb&fm=jpg&q=85";
  const why = settings?.why_us || [];

  const title = lang === "ar" ? hero.title_ar : hero.title_en;
  const subtitle = lang === "ar" ? hero.subtitle_ar : hero.subtitle_en;
  const badge = lang === "ar" ? hero.badge_ar : hero.badge_en;
  const cta = lang === "ar" ? hero.cta_ar : hero.cta_en;

  return (
    <section className="relative overflow-hidden pt-24 sm:pt-32 pb-8" data-testid="hero-section">
      <div className="absolute -top-24 end-0 h-96 w-96 rounded-full cb-gradient opacity-20 blur-3xl blob pointer-events-none" />
      <div className="absolute top-1/2 start-0 h-72 w-72 rounded-full bg-accent opacity-10 blur-3xl blob pointer-events-none" />

      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary text-secondary-foreground text-xs sm:text-sm font-semibold mb-5 sm:mb-6">
            <Sparkles className="h-4 w-4" />
            {badge}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-foreground">
            {title}
          </h1>
          <p className="mt-4 sm:mt-6 text-base sm:text-xl text-muted-foreground leading-relaxed max-w-xl">{subtitle}</p>
          <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button
              size="lg"
              onClick={() => nav("/book")}
              data-testid="hero-book-now-cta"
              className="w-full sm:w-auto rounded-full px-8 h-14 text-base font-bold group"
            >
              {cta || t.common.bookNow}
              <ArrowLeft className="h-5 w-5 ms-2 rtl-flip transition-transform group-hover:-translate-x-1" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => nav("/services")}
              data-testid="hero-services-cta"
              className="w-full sm:w-auto rounded-full px-8 h-14 text-base font-semibold border-2"
            >
              {t.nav.services}
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative"
        >
          <div className="relative rounded-3xl overflow-hidden cb-card-shadow aspect-[4/3] sm:aspect-square">
            <img src={img} alt="Cloud Beauty Spa" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
          </div>
        </motion.div>
      </div>

      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto mt-10 sm:mt-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {why.map((w, i) => {
            const Icon = ICONS[w.icon] || Sparkles;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + i * 0.1 }}
                className="rounded-2xl bg-card cb-card-shadow p-4 sm:p-5 text-center"
                data-testid={`why-card-${i}`}
              >
                <span className="inline-grid place-items-center h-12 w-12 rounded-2xl bg-secondary text-primary mb-3">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="font-semibold text-sm sm:text-base text-foreground">{L(w, "title")}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">{L(w, "desc")}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
